import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {FieldError} from '@/components/field-error'
import {Hint} from '@/components/ui/hint'
import {MicroLabel} from '@/components/period-selector'
import {useMarcarEjecucion, type PlanActividad} from '@/hooks/sgsst/plan/use-plan-trabajo'
import {mintUploadTarget, uploadToStorage, useRegisterEvidence} from '@/hooks/sgsst/evidencias/use-evidence'
import {translateApiError} from '@/lib/errors'
import {localToday} from '@/lib/risk'
import {validateRequired} from '@/lib/validation'

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

/** Una casilla ya pasada no se ejecutó «hoy»: prellenar la fecha sería inventar el dato. */
function casillaYaPaso(periodYear: number, mes: number, hoy: string): boolean {
  const [anio, mesHoy] = hoy.split('-').map(Number)
  return periodYear < anio || (periodYear === anio && mes < mesHoy)
}

/** The soporte is optional and never blocks: a failed upload still marks the month. */
export function MarcarEjecucionDialog({
  businessId,
  planId,
  periodYear,
  actividad,
  mes,
  onClose,
}: {
  businessId: string
  planId: string
  periodYear: number
  actividad: PlanActividad
  mes: number
  onClose: () => void
}) {
  const {t} = useTranslation()
  const marcar = useMarcarEjecucion(businessId, planId)
  const registrar = useRegisterEvidence(businessId)
  const [file, setFile] = useState<File | null>(null)
  const [subiendo, setSubiendo] = useState(false)
  const [avisoSoporte, setAvisoSoporte] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // nosemgrep: browser-clock-business-date -- date input default/max, legitimate data-entry: la regla la decide el backend contra bogota_today(), esto solo prellena y acota el campo
  const hoy = localToday()
  const pasada = casillaYaPaso(periodYear, mes, hoy)
  const [fecha, setFecha] = useState(pasada ? '' : hoy)
  const [errorFecha, setErrorFecha] = useState<string | undefined>(undefined)

  const estandar = actividad.referencia
  const mesLabel = MESES[mes - 1]
  const pending = marcar.isPending || subiendo

  function validarFecha(valor: string): string | undefined {
    const vacio = validateRequired(valor)
    if (vacio) return vacio
    if (valor > hoy) return 'planTrabajo.marcarDialog.fechaFutura'
    if (valor < `${periodYear}-01-01`) return 'planTrabajo.marcarDialog.fechaAntesDelPlan'
    return undefined
  }

  async function confirmar() {
    const problema = validarFecha(fecha)
    setErrorFecha(problema)
    if (problema) return
    setError(null)
    setAvisoSoporte(null)
    if (file && estandar) {
      setSubiendo(true)
      try {
        const target = await mintUploadTarget(businessId, file)
        await uploadToStorage(target, file, () => {})
        await registrar.mutateAsync({
          standard_code: estandar,
          object_key: target.object_key,
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size,
          employee_ids: [],
        })
      } catch (err) {
        setAvisoSoporte(translateApiError(err))
      } finally {
        setSubiendo(false)
      }
    }
    marcar.mutate(
      {actividadId: actividad.id, mes, fecha},
      {
        onSuccess: onClose,
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="marcar-ejecucion-dialog">
        <DialogHeader>
          <DialogTitle>{t('planTrabajo.marcarDialog.titulo', {mes: mesLabel})}</DialogTitle>
          <DialogDescription>
            {t('planTrabajo.marcarDialog.cuerpo', {actividad: actividad.titulo, mes: mesLabel})}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <MicroLabel>{t('planTrabajo.marcarDialog.fechaLabel')}</MicroLabel>
          <Hint testId="marcar-fecha-ayuda">
            {pasada
              ? t('planTrabajo.marcarDialog.fechaAyudaPasada', {mes: mesLabel})
              : t('planTrabajo.marcarDialog.fechaAyudaHoy')}
          </Hint>
          <input
            type="date"
            value={fecha}
            data-testid="marcar-fecha"
            aria-label={t('planTrabajo.marcarDialog.fechaLabel')}
            max={hoy}
            min={`${periodYear}-01-01`}
            aria-invalid={!!errorFecha}
            onChange={(e) => {
              setFecha(e.target.value)
              setErrorFecha(undefined)
            }}
            onBlur={(e) => setErrorFecha(validarFecha(e.target.value))}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 font-mono text-sm shadow-sm focus:border-ring focus:outline-none"
          />
          <FieldError error={errorFecha} />
        </div>

        {estandar && (
          <div className="space-y-1.5">
            <MicroLabel>{t('planTrabajo.marcarDialog.soporteLabel')}</MicroLabel>
            <Hint>{t('planTrabajo.marcarDialog.soporteAyuda', {code: estandar})}</Hint>
            <input
              type="file"
              data-testid="marcar-soporte-input"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-[13px] text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-1.5 file:text-[12px] file:font-medium"
            />
            {file && (
              <button
                type="button"
                className="text-[12px] text-muted-foreground underline"
                data-testid="marcar-soporte-quitar"
                onClick={() => setFile(null)}>
                {t('planTrabajo.marcarDialog.quitarArchivo')}
              </button>
            )}
          </div>
        )}

        {avisoSoporte && (
          <p className="text-[12.5px] text-destructive" data-testid="marcar-soporte-error">
            {t('planTrabajo.marcarDialog.errorSoporte')} ({avisoSoporte})
          </p>
        )}
        {error && (
          <p className="text-[12.5px] text-destructive" data-testid="marcar-error">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button data-testid="marcar-confirmar" disabled={pending} onClick={() => void confirmar()}>
            {pending ? t('common.saving') : t('planTrabajo.marcarDialog.confirmar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
